/**
 * Which bin the Add box files into (D222): Auto lets the AI pick, the rest say which. Remembered
 * on this device like the bulbs' tiers (`bulbTier.ts`) - the chip you last used is the chip you
 * want next time.
 */

import { useCallback, useSyncExternalStore } from "react";

export type AddBin = "auto" | "task" | "note" | "project" | "context";

export const ADD_BINS: readonly { bin: AddBin; label: string }[] = [
  { bin: "auto", label: "Auto" },
  { bin: "task", label: "Task" },
  { bin: "note", label: "Note" },
  { bin: "project", label: "Project" },
  { bin: "context", label: "Context" },
];

const KEY = "efss.addBin";
const listeners = new Set<() => void>();

function read(): AddBin {
  try {
    const stored = window.localStorage.getItem(KEY);
    return ADD_BINS.some((entry) => entry.bin === stored) ? (stored as AddBin) : "task";
  } catch {
    return "task"; // storage blocked: the box behaves as it always did
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

/** The chips on offer. Auto needs an AI to pick; Context only matters where the server runs AI
 * to read it into (an account with no model of its own yet keeps it). */
export function availableBins(aiConfigured: boolean, serverAi: boolean): AddBin[] {
  return ADD_BINS.map((entry) => entry.bin).filter(
    (bin) => (bin !== "auto" || aiConfigured) && (bin !== "context" || serverAi),
  );
}

/**
 * The chip in use. A stored chip that is not on offer reads as Task meanwhile - without being
 * forgotten, so it returns if an AI is set up later.
 */
export function useAddBin(
  aiConfigured: boolean,
  serverAi: boolean,
): { bin: AddBin; setBin: (bin: AddBin) => void } {
  const stored = useSyncExternalStore(subscribe, read, () => "task" as AddBin);
  const setBin = useCallback((next: AddBin) => {
    try {
      window.localStorage.setItem(KEY, next);
    } catch {
      /* ignore - the choice just will not persist */
    }
    for (const listener of listeners) listener();
  }, []);
  return { bin: availableBins(aiConfigured, serverAi).includes(stored) ? stored : "task", setBin };
}
